import { useState, useEffect, useRef } from "react";
import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Trophy,
  Flame,
  Star,
  Users,
  TrendingUp,
  CheckCircle2,
  Shield,
  Loader2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { StaffBadge } from "@/components/StaffBadge";

const ALLOWED_EMOJIS = ["👍", "❤️", "🔥", "🏆", "😮"];

interface Reaction {
  emoji: string;
  count: number;
  hasReacted: boolean;
}

interface ActivityItem {
  id: string;
  userId: string;
  activityType: string;
  payload: Record<string, any> | null;
  createdAt: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl?: string;
  role?: string;
  reactions?: Reaction[];
}

interface FeedPage {
  items: ActivityItem[];
  hasMore: boolean;
}

const ACTIVITY_CONFIG: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; color: string; label: (p: any) => string }
> = {
  badge_earned: {
    icon: Shield,
    color: "text-purple-500",
    label: (p) => `earned the "${p.badgeName ?? "badge"}" badge`,
  },
  challenge_won: {
    icon: Trophy,
    color: "text-yellow-500",
    label: (p) => `beat ${p.opponentName ?? "an opponent"} in a challenge (${p.myScore ?? "?"} vs ${p.opponentScore ?? "?"})`,
  },
  challenge_lost: {
    icon: Trophy,
    color: "text-slate-400",
    label: (p) => `played a challenge against ${p.opponentName ?? "an opponent"}`,
  },
  streak_milestone: {
    icon: Flame,
    color: "text-orange-500",
    label: (p) => `hit a ${p.streakDays ?? "?"}-day study streak`,
  },
  level_up: {
    icon: TrendingUp,
    color: "text-blue-500",
    label: (p) => `reached Level ${p.newLevel ?? "?"}`,
  },
  correct_milestone: {
    icon: CheckCircle2,
    color: "text-green-500",
    label: (p) => `answered ${p.totalCorrect ?? "?"} questions correctly`,
  },
  new_follower: {
    icon: Users,
    color: "text-pink-500",
    label: (p) => `gained a new follower: ${p.followerName ?? "someone"}`,
  },
};

function ReactionBar({ item }: { item: ActivityItem }) {
  const reactions = item.reactions ?? [];
  const myReacted = reactions.find(r => r.hasReacted)?.emoji ?? null;

  const reactMutation = useMutation({
    mutationFn: async (emoji: string) => {
      const res = await apiRequest("POST", `/api/social/feed/${item.id}/react`, { emoji });
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/social/feed"] }),
  });

  const unreactMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/social/feed/${item.id}/react`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/social/feed"] }),
  });

  const handleEmojiClick = (emoji: string) => {
    if (reactMutation.isPending || unreactMutation.isPending) return;
    if (myReacted === emoji) {
      unreactMutation.mutate();
    } else {
      reactMutation.mutate(emoji);
    }
  };

  // Build a map from emoji to count (include all allowed emojis)
  const countMap = new Map(reactions.map(r => [r.emoji, r.count]));

  return (
    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
      {ALLOWED_EMOJIS.map(emoji => {
        const c = countMap.get(emoji) ?? 0;
        const active = myReacted === emoji;
        return (
          <button
            key={emoji}
            onClick={() => handleEmojiClick(emoji)}
            className={`inline-flex items-center gap-0.5 text-xs px-2.5 py-1.5 rounded-full border transition-colors select-none touch-manipulation min-h-[32px]
              ${active
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "border-border bg-muted/40 text-muted-foreground hover:border-primary/50 hover:bg-primary/5"
              }`}
          >
            <span>{emoji}</span>
            {c > 0 && <span className="ml-0.5">{c}</span>}
          </button>
        );
      })}
    </div>
  );
}

function ActivityRow({ item }: { item: ActivityItem }) {
  const [, navigate] = useLocation();
  const config = ACTIVITY_CONFIG[item.activityType] ?? {
    icon: Star,
    color: "text-muted-foreground",
    label: () => item.activityType,
  };
  const Icon = config.icon;

  return (
    <div className="py-3">
      <div className="flex items-start gap-3">
        <div
          className="relative flex-shrink-0 cursor-pointer"
          onClick={() => navigate(`/user/${item.userId}`)}
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src={item.profilePhotoUrl || ""} />
            <AvatarFallback className="bg-primary/10 text-xs">
              {item.firstName?.[0]}{item.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <span className={`absolute -bottom-1 -right-1 p-0.5 rounded-full bg-background ${config.color}`}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            <span
              className="font-semibold cursor-pointer hover:underline"
              onClick={() => navigate(`/user/${item.userId}`)}
            >
              {item.firstName} {item.lastName}
            </span>
            {item.role === 'admin' && <>{" "}<StaffBadge /></>}
            {" "}
            <span className="text-muted-foreground">{config.label(item.payload ?? {})}</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {item.createdAt ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true }) : ""}
          </p>
          <ReactionBar item={item} />
        </div>
      </div>
    </div>
  );
}

function groupByDay(items: ActivityItem[]): { date: string; items: ActivityItem[] }[] {
  const map = new Map<string, ActivityItem[]>();
  for (const item of items) {
    const day = item.createdAt
      ? new Date(item.createdAt).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })
      : "Unknown";
    if (!map.has(day)) map.set(day, []);
    map.get(day)!.push(item);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
}

export default function ActivityFeed() {
  const [, navigate] = useLocation();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // NOTE: queryKey is namespaced with "infinite" so it does not collide with
  // the plain useQuery<{ items, hasMore }> in MyProfile.tsx. Sharing the same
  // key would crash InfiniteQueryObserver because the cache shape differs
  // (it expects { pages, pageParams } and would read .length on undefined).
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery<FeedPage>({
      queryKey: ["/api/social/feed", "infinite"],
      queryFn: async ({ pageParam = 0 }) => {
        const res = await fetch(`/api/social/feed?offset=${pageParam}`, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to fetch feed");
        return res.json();
      },
      getNextPageParam: (lastPage, pages) =>
        lastPage?.hasMore ? (pages?.length ?? 0) * 30 : undefined,
      initialPageParam: 0,
    });

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allItems = data?.pages.flatMap(p => p.items) ?? [];
  const grouped = groupByDay(allItems);

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>

      <h1 className="text-2xl font-bold mb-6">Activity Feed</h1>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : allItems.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-40" />
            <h3 className="font-semibold mb-2">Nothing here yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Follow other students to see their achievements and activity here.
            </p>
            <Button onClick={() => navigate("/leaderboard")} variant="outline">
              Explore Leaderboard
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ date, items }) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-2">
                <Badge variant="outline" className="text-xs font-normal">
                  {date}
                </Badge>
              </div>
              <Card>
                <CardContent className="p-4 divide-y divide-border">
                  {items.map(item => (
                    <ActivityRow key={item.id} item={item} />
                  ))}
                </CardContent>
              </Card>
            </div>
          ))}

          {/* Infinite scroll sentinel */}
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage && (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
